import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Text,
} from '@billda/design-system';

export interface CollisionItem {
  file: File;
  originalName: string;
  suggestedName: string;
}

type Decision = 'pending' | 'keep-both' | 'skipped';

interface BatchCollisionDialogProps {
  open: boolean;
  collisions: CollisionItem[];
  onResolve: (resolved: Array<{ file: File; fileName: string }>) => void;
  onCancel: () => void;
}

export function BatchCollisionDialog({
  open,
  collisions,
  onResolve,
  onCancel,
}: BatchCollisionDialogProps) {
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  const updateDecision = (originalName: string, decision: Decision) => {
    setDecisions(prev => ({ ...prev, [originalName]: decision }));
  };

  const allDecided = collisions.every(
    c => (decisions[c.originalName] ?? 'pending') !== 'pending'
  );

  const handleContinue = () => {
    const resolved = collisions
      .filter(c => decisions[c.originalName] === 'keep-both')
      .map(c => ({ file: c.file, fileName: c.suggestedName }));
    setDecisions({});
    onResolve(resolved);
  };

  const handleCancel = () => {
    setDecisions({});
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && handleCancel()}>
      <DialogContent
        data-testid="batch-collision-dialog"
        className="max-w-sm sm:max-w-md"
      >
        <DialogHeader>Some files already exist</DialogHeader>
        <DialogBody>
          {collisions.map(collision => {
            const decision = decisions[collision.originalName] ?? 'pending';
            return (
              <div
                key={collision.originalName}
                className="flex items-center justify-between border-b border-border-default py-2 last:border-b-0"
              >
                <Text
                  variant="body2"
                  noWrap
                  className={`mr-2 flex-1 ${
                    decision === 'skipped'
                      ? 'text-content-disabled line-through'
                      : 'text-content-primary'
                  }`}
                >
                  {collision.originalName}
                </Text>
                <div className="flex shrink-0 gap-2">
                  <Button
                    data-testid="collision-keep-both"
                    size="sm"
                    variant={decision === 'keep-both' ? 'neutral' : 'outline'}
                    onClick={() =>
                      updateDecision(collision.originalName, 'keep-both')
                    }
                  >
                    Keep both
                  </Button>
                  <Button
                    data-testid="collision-skip"
                    size="sm"
                    variant={decision === 'skipped' ? 'neutral' : 'outline'}
                    onClick={() =>
                      updateDecision(collision.originalName, 'skipped')
                    }
                  >
                    Skip
                  </Button>
                </div>
              </div>
            );
          })}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-testid="collision-continue"
            variant="neutral"
            onClick={handleContinue}
            disabled={!allDecided}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
